/**
 * ControlPanel API (`/synced-texts`) クライアント。
 *
 * 共有テキストマスタ (SyncedTextMaster) を CRUD する。放送地点の任意テキスト放送が
 * **参照** (コピーしない) する本文を線区別に管理する。
 *
 * - すべての呼び出しに `Authorization: Bearer <Cognito ID Token>` を付与する。
 * - 個別アイテムは `(route_id, template_id)` の複合キーなので 2 階層のパスになる
 *   (`synced-texts/{route_id}/{template_id}`)。`route_id` は日本語を含むため
 *   **必ず URL エンコード**して送る。
 * - 削除は参照中なら 409 (`ApiError.body` に参照元 `references` が入る)。
 */
import type { AppConfig } from '../config';

import { ApiError, joinUrl } from './common';
import type {
  ApiErrorBody,
  SyncedTextDeleteResponse,
  SyncedTextListResponse,
  SyncedTextSaveResult,
} from './types';

export interface SyncedTextsClientOptions {
  /** Cognito ID トークンを取得する関数 (期限切れ時は AuthProvider 側で自動更新済み) */
  getIdToken: () => Promise<string>;
  /** AppConfig (apiUrl 取得用) */
  config: AppConfig;
  /** テスト時に差し込む fetch 実装 */
  fetchImpl?: typeof fetch;
}

export class SyncedTextsClient {
  private readonly options: SyncedTextsClientOptions;

  constructor(options: SyncedTextsClientOptions) {
    this.options = options;
  }

  /**
   * GET /synced-texts (共有テキスト一覧)
   *
   * 並びは全線区共通 → 各線区 → 本文。
   *
   * @param routeId 指定すると「その線区 + 全線区共通」のみに絞り込む (省略時は全件)
   */
  async list(routeId?: string): Promise<SyncedTextListResponse> {
    const query = routeId ? `?route_id=${encodeURIComponent(routeId)}` : '';
    return this.request<SyncedTextListResponse>('GET', `synced-texts${query}`);
  }

  /**
   * POST /synced-texts (共有テキストを登録)
   *
   * `template_id` はサーバが採番する。同じ線区に同じ本文があっても保存され、
   * レスポンスの `warnings` に `duplicate_text` が入る。
   */
  async create(params: {
    routeId: string;
    title: string;
    text: string;
    note?: string;
  }): Promise<SyncedTextSaveResult> {
    return this.request<SyncedTextSaveResult>('POST', 'synced-texts', {
      route_id: params.routeId,
      title: params.title,
      text: params.text,
      ...(params.note !== undefined && { note: params.note }),
    });
  }

  /**
   * PUT /synced-texts/{route_id}/{template_id} (本文を更新)
   *
   * `route_id` はキーなので変更できない (変えたいときは削除して登録し直す)。
   * 参照している放送地点は編集不要で、次回の放送生成で新しい本文になる。
   */
  async update(
    routeId: string,
    templateId: string,
    params: { text: string; title?: string; note?: string },
  ): Promise<SyncedTextSaveResult> {
    return this.request<SyncedTextSaveResult>('PUT', this.itemPath(routeId, templateId), {
      text: params.text,
      ...(params.title !== undefined && { title: params.title }),
      ...(params.note !== undefined && { note: params.note }),
    });
  }

  /** DELETE /synced-texts/{route_id}/{template_id} (共有テキストを削除 / 参照中は 409) */
  async remove(routeId: string, templateId: string): Promise<SyncedTextDeleteResponse> {
    return this.request<SyncedTextDeleteResponse>('DELETE', this.itemPath(routeId, templateId));
  }

  // ---------------------------------------------------------------------
  // 内部実装
  // ---------------------------------------------------------------------

  /** 個別アイテムのパス (2 階層のパスパラメータ) を組み立てる。 */
  private itemPath(routeId: string, templateId: string): string {
    return `synced-texts/${encodeURIComponent(routeId)}/${encodeURIComponent(templateId)}`;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = joinUrl(this.options.config.apiUrl, path);
    const idToken = await this.options.getIdToken();
    const fetchImpl = this.options.fetchImpl ?? fetch;

    const res = await fetchImpl(url, {
      method,
      headers: {
        ...(body !== undefined && { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${idToken}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      let errorBody: ApiErrorBody | undefined;
      try {
        errorBody = (await res.json()) as ApiErrorBody;
      } catch {
        // body が JSON で無い場合は無視
      }
      const message = errorBody?.message ?? `API request failed: HTTP ${res.status}`;
      throw new ApiError(res.status, message, errorBody);
    }

    return (await res.json()) as T;
  }
}

export { ApiError };
